import { Alert, Typography } from '@mui/joy';
import { selectClasses, SelectStaticProps } from '@mui/joy/Select';
import { Info } from '@phosphor-icons/react';
import { mergeProps } from '@react-aria/utils';
import clsx from 'clsx';
import { ComponentPropsWithoutRef, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { z } from 'zod';

import { getUserTypeName } from '../auth/constants';

import { Option, Select } from '~/components/ui/select';
import { usePartnerContext } from '~/contexts/partner/partnerContext';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { isMutationSuccess } from '~/services/api/rest-api';
import { TenantService } from '~/services/api/tenant/endpoints';
import { UserTenant } from '~/services/api/tenant/types';
import { UserService } from '~/services/api/users/endpoints';
import { useAuthSlice } from '~/slices/auth';
import { useForm } from '~/utils/forms';

const schema = z
  .object({
    selection: z.enum(['all', 'customer']),
    company: z.coerce.number().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.selection === 'customer' && !data.company) {
      return ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['company'],
        message: 'Company is required when selecting customer.',
      });
    }
    return data;
  });

type ArrayElement<T> = T extends (infer U)[] ? U : never;

export default function CompanySelector({
  onChange,
  ...props
}: Omit<ComponentPropsWithoutRef<typeof Select>, 'onChange'> & {
  onChange?: (value?: ArrayElement<UserTenant['assigned_tenants']>) => void;
}) {
  const subTenants =
    TenantService.endpoints.userTenant.useQuery().data?.assigned_tenants ?? [];
  // const hasValue = !!props.value || !!props.defaultValue;
  const action: SelectStaticProps['action'] = useRef(null);

  return (
    <Select
      {...mergeProps(
        {
          size: 'lg',
          variant: 'outlined',
          placeholder: 'Select Company',
          action,
        },
        props,
      )}
      sx={{
        ...(props.sx ?? {}),
        ...{
          [`:not(${selectClasses.focusVisible})[data-selected=false]`]: {
            backgroundColor: (theme) => theme.palette.neutral[100],
          },
        },
      }}
      onChange={(_, value) => {
        if (!value || !onChange) return;

        const matchingTenant = subTenants.find((tenant) => tenant.id === value);
        if (matchingTenant) {
          onChange(matchingTenant);
        }
      }}
    >
      {subTenants.map((tenant) => (
        <Option key={tenant.id} value={tenant.id}>
          {tenant.name}
        </Option>
      ))}
    </Select>
  );
}

export function SwitchCompanySelect(
  props: Pick<
    ComponentPropsWithoutRef<typeof CompanySelector>,
    'size' | 'variant' | 'color'
  >,
) {
  const intl = useIntl();
  const authSlice = useAuthSlice();
  const [update, mutation] =
    UserService.endpoints.updateUserProfile.useMutation();
  const { createToastNotification } = useToastNotificationContext();
  const partner = usePartnerContext();
  const value = z.coerce
    .number()
    .positive()
    .catch(0)
    .parse(partner.selectedTenant.id);

  return (
    <CompanySelector
      value={value ? value : null}
      onChange={async (tenant) => {
        if (!tenant) {
          return partner.reset();
        }

        const result = await update({ tenant_id: tenant.id });
        if (isMutationSuccess(result)) {
          partner.setSelectedTenant(tenant);
          authSlice.setCredentials({ slug: tenant.slug });
          localStorage.setItem('slug', tenant.slug);
        } else {
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'Failed to switch company' }),
            'error',
          );
        }
      }}
      disabled={mutation.isLoading}
      {...props}
    />
  );
}

interface CompanySwitchToggleProps {
  onCompanyChange: (id?: number) => void;
}

export function CompanySwitchToggle({
  onCompanyChange,
}: CompanySwitchToggleProps) {
  const location = useLocation();
  const partner = usePartnerContext();
  const hasAllOption =
    location.pathname === '/customer/dashboard' ||
    location.pathname === '/customer/reports';
  const form = useForm({
    defaultValues: {
      selection: 'customer',
      company: partner.selectedTenant.id,
    },
    schema,
  });
  const onSubmitHandler = form.handleSubmit((data) => {
    if (data.selection === 'customer' && data.company) {
      onCompanyChange(data.company);
    } else {
      onCompanyChange(undefined);
    }
  });
  const selection = form.watch('selection');
  const subTenants =
    TenantService.endpoints.userTenant.useQuery().data?.assigned_tenants ?? [];

  return (
    <form onChange={onSubmitHandler}>
      <div className='flex w-96 bg-zinc-200 rounded-lg p-1 gap-x-1'>
        {hasAllOption && (
          <Controller
            control={form.control}
            name='selection'
            render={({ field }) => (
              <label
                className={clsx([
                  'flex-1 focus-within:ring-2 ring-inset focus-within:ring-brand-primary-500 cursor-pointer h-10 flex items-center justify-center relative rounded-md border',
                  field.value === 'all' ? 'bg-white' : 'bg-transparent',
                ])}
              >
                <input
                  ref={field.ref}
                  onChange={(e) => {
                    form.setValue('company', 0, {
                      shouldValidate: true,
                    });
                    field.onChange(e.target.checked ? 'all' : 'customer');
                    onSubmitHandler();
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                  type='radio'
                  checked={field.value === 'all'}
                  className='absolute inset-0 opacity-0'
                />
                <span className='text-base'>All</span>
              </label>
            )}
          />
        )}
        <div className='flex-1 h-10'>
          <Controller
            control={form.control}
            name='company'
            render={({ field }) => (
              <Select
                size='md'
                data-selected={JSON.stringify(selection === 'customer')}
                placeholder='Select Company'
                sx={{
                  height: '100%',
                  [`:not(${selectClasses.focusVisible})[data-selected=false]`]:
                    {
                      backgroundColor: (theme) => theme.palette.neutral[100],
                    },
                }}
                {...field}
                onChange={(_e, value) => {
                  form.setValue('selection', value ? 'customer' : 'all', {
                    shouldValidate: true,
                  });
                  field.onChange(value ? value : '');
                  onSubmitHandler();
                }}
              >
                {subTenants.map((tenant) => (
                  <Option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </Option>
                ))}
              </Select>
            )}
          />
        </div>
      </div>
    </form>
  );
}

function SelectedCompanyFallback() {
  const intl = useIntl();
  return (
    <Alert variant='outlined'>
      <span className='flex items-start self-stretch'>
        <Info />
      </span>
      <div>
        <Typography level='title-lg'>
          {intl.formatMessage({ defaultMessage: 'Selected Company Required' })}
        </Typography>
        <Typography level='body-sm'>
          {intl.formatMessage({
            defaultMessage:
              'A selected company is required to manage or view this data.',
          })}
        </Typography>
      </div>
      <div className='ml-auto'>
        <SwitchCompanySelect variant='outlined' />
      </div>
    </Alert>
  );
}

/**
 * We only enforce this when the logged in user is a partner
 */
export function RequireSelectedCompany({
  fallback = <SelectedCompanyFallback />,
  children,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { selectedTenant } = usePartnerContext();
  const hasSelected = z.coerce
    .number()
    .positive()
    .safeParse(selectedTenant.id).success;
  const userType = useAuthSlice().auth.user.type;
  const userTypeName = getUserTypeName(userType);
  const isPartnerType =
    userTypeName === 'PARTNER_USER' || userTypeName === 'PARTNER_ADMIN';

  if (!isPartnerType) {
    return children;
  }

  if (!hasSelected) {
    return fallback;
  }
  return children;
}
